/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import memoizeOne from 'memoize-one';
import { createLocalExpirableStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import type { ExpirableWebStorageProvider } from '@jira/platform__browser-storage-providers/src/types.tsx';

const getStore = memoizeOne(() => createLocalExpirableStorageProvider('onboarding'));

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

type StoredItem = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	value: any;
	accessedAt: number | null;
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const createStoredItem = (value: any, accessedAt?: number): StoredItem => ({
	value,
	accessedAt: accessedAt ?? null,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const isItemValid = (item: any): item is StoredItem =>
	typeof item === 'object' &&
	!Array.isArray(item) &&
	item !== null &&
	'value' in item &&
	'accessedAt' in item;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const set = (key: string, value: any): void => {
	const store = getStore();
	const serializedValue = JSON.stringify(createStoredItem(value));
	const expirationTime = Date.now() + ONE_WEEK_IN_MS;
	// @browser-storage setter
	store.set(key, serializedValue, expirationTime);
};
/**
 * Retrieve an item from onboarding browser storage (local storage).
 * Sets the accessedAt property of this item in storage to Date.now().
 */
export const get = (key: string): StoredItem | null => {
	const store = getStore();
	const item = store.get(key);
	try {
		const parsed = item && JSON.parse(item);
		if (isItemValid(parsed)) {
			const serializedValue = JSON.stringify(createStoredItem(parsed.value, Date.now()));
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const currentExpiration = (store as ExpirableWebStorageProvider)?.getExpiration(key);
			if (currentExpiration) {
				// @browser-storage setter
				store.set(key, serializedValue, currentExpiration);
			} else {
				const expirationTime = Date.now() + ONE_WEEK_IN_MS;
				// @browser-storage setter
				store.set(key, serializedValue, expirationTime);
			}
			return parsed;
		}
		return null;
	} catch {
		return null;
	}
};
